// Code 39 barcode → SVG markup string.
//
// Ported faithfully from the Library module's Code39BarcodeSvg so the print
// flows (which build raw HTML for a popup window) can embed a barcode without a
// React component. Self-contained — no dependencies, no DOM.

const CODE39_PATTERNS: Record<string, string> = {
  "0": "nnnwwnwnn", "1": "wnnwnnnnw", "2": "nnwwnnnnw", "3": "wnwwnnnnn",
  "4": "nnnwwnnnw", "5": "wnnwwnnnn", "6": "nnwwwnnnn", "7": "nnnwnnwnw",
  "8": "wnnwnnwnn", "9": "nnwwnnwnn",
  A: "wnnnnwnnw", B: "nnwnnwnnw", C: "wnwnnwnnn", D: "nnnnwwnnw", E: "wnnnwwnnn",
  F: "nnwnwwnnn", G: "nnnnnwwnw", H: "wnnnnwwnn", I: "nnwnnwwnn", J: "nnnnwwwnn",
  K: "wnnnnnnww", L: "nnwnnnnww", M: "wnwnnnnwn", N: "nnnnwnnww", O: "wnnnwnnwn",
  P: "nnwnwnnwn", Q: "nnnnnnwww", R: "wnnnnnwwn", S: "nnwnnnwwn", T: "nnnnwnwwn",
  U: "wwnnnnnnw", V: "nwwnnnnnw", W: "wwwnnnnnn", X: "nwnnwnnnw", Y: "wwnnwnnnn",
  Z: "nwwnwnnnn",
  "-": "nwnnnnwnw", ".": "wwnnnnwnn", " ": "nwwnnnwnn", $: "nwnwnwnnn",
  "/": "nwnwnnnwn", "+": "nwnnnwnwn", "%": "nnnwnwnwn",
  "*": "nwnnwnwnn", // start/stop
}

export const sanitizeCode39 = (raw: string): { value: string; isValid: boolean } => {
  const normalized = String(raw || "").trim().replace(/_/g, "-").toUpperCase()
  // Code 39 supports: A-Z 0-9 space - . $ / + %
  const isValid = /^[0-9A-Z \-.\$/+%]*$/.test(normalized)
  return { value: normalized, isValid }
}

// Returns SVG markup (string). Empty string if the value can't be encoded.
export const code39SvgString = (
  value: string,
  { height = 45, unit = 2, showText = true }: { height?: number; unit?: number; showText?: boolean } = {}
): string => {
  const { value: clean, isValid } = sanitizeCode39(value)
  if (!isValid || !clean) return ""

  const content = `*${clean}*`
  const narrow = 1 * unit
  const wide = 3 * unit
  const interCharGap = 1 * unit
  const quietZone = 6 * unit
  const barHeight = height
  const textHeight = showText ? 16 : 0

  let x = quietZone
  const rects: Array<{ x: number; w: number }> = []
  for (let ci = 0; ci < content.length; ci++) {
    const pattern = CODE39_PATTERNS[content[ci]]
    if (!pattern) continue
    for (let i = 0; i < pattern.length; i++) {
      const isBar = i % 2 === 0
      const w = pattern[i] === "w" ? wide : narrow
      if (isBar) rects.push({ x, w })
      x += w
    }
    x += interCharGap // gap between characters
  }

  const totalWidth = x + quietZone
  const totalHeight = barHeight + textHeight
  const bars = rects
    .map(r => `<rect x="${r.x}" y="0" width="${r.w}" height="${barHeight}" fill="#000"/>`)
    .join("")
  const text = showText
    ? `<text x="${totalWidth / 2}" y="${barHeight + 13}" text-anchor="middle" font-family="monospace" font-size="12" fill="#000">${clean}</text>`
    : ""

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}"><rect x="0" y="0" width="${totalWidth}" height="${totalHeight}" fill="#fff"/>${bars}${text}</svg>`
}
